/**
 * ImportBrowser — Orchestrator for filesystem browsing
 *
 * Manages folder selection, navigation, view mode toggle,
 * directory size computation, and file selection state.
 *
 * @module @humanizer/studio/components/import/ImportBrowser
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import type { DirEntryWithStats } from '../../types/electron';
import { TreemapView } from './TreemapView';
import { FileListView } from './FileListView';

export interface ImportBrowserProps {
  onSelectionChange: (paths: Set<string>) => void;
  onDetect: () => void;
  onDetectFolder: (folderPath: string) => void;
  onDetectFiles: (filePaths: string[]) => void;
  selectedPaths: Set<string>;
}

type ViewMode = 'treemap' | 'list';

function formatSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

export function ImportBrowser({
  onSelectionChange,
  onDetect,
  onDetectFolder,
  onDetectFiles,
  selectedPaths,
}: ImportBrowserProps): React.ReactElement {
  const [rootPath, setRootPath] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState<string | null>(null);
  const [pathHistory, setPathHistory] = useState<string[]>([]);
  const [entries, setEntries] = useState<DirEntryWithStats[]>([]);
  const [dirSizes, setDirSizes] = useState<Map<string, number>>(new Map());
  const [viewMode, setViewMode] = useState<ViewMode>('treemap');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterExt, setFilterExt] = useState<string | null>(null);

  const dirSizeAbort = useRef<AbortController | null>(null);

  // Compute unique file extensions for filter chips
  const extensions = React.useMemo(() => {
    const exts = new Map<string, number>();
    for (const entry of entries) {
      if (!entry.isDirectory && entry.name.includes('.')) {
        const ext = entry.name.split('.').pop()?.toLowerCase() || '';
        exts.set(ext, (exts.get(ext) || 0) + 1);
      }
    }
    return Array.from(exts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12);
  }, [entries]);

  // Filtered entries
  const filteredEntries = React.useMemo(() => {
    if (!filterExt) return entries;
    return entries.filter(e => {
      if (e.isDirectory) return true;
      const ext = e.name.split('.').pop()?.toLowerCase() || '';
      return ext === filterExt;
    });
  }, [entries, filterExt]);

  // Load entries when currentPath changes
  const loadEntries = useCallback(async (dirPath: string) => {
    const api = window.electronImport;
    if (!api) return;

    setLoading(true);
    setError(null);
    setFilterExt(null);

    try {
      const items = await api.readDir(dirPath);
      setEntries(items);

      // Cancel previous dir size computations
      dirSizeAbort.current?.abort();
      dirSizeAbort.current = new AbortController();
      const signal = dirSizeAbort.current.signal;

      // Lazily compute directory sizes
      const dirs = items.filter(e => e.isDirectory);
      setDirSizes(new Map());

      for (const dir of dirs) {
        if (signal.aborted) break;
        try {
          const result = await api.dirSize(dir.path);
          if (signal.aborted) break;
          setDirSizes(prev => {
            const next = new Map(prev);
            next.set(dir.path, result.size);
            return next;
          });
        } catch {
          // Skip directories that can't be read
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to read directory');
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle folder selection
  const handleSelectFolder = useCallback(async () => {
    const api = window.electronImport;
    if (!api) return;

    const root = await api.selectRoot();
    if (!root) return;

    setRootPath(root);
    setCurrentPath(root);
    setPathHistory([]);
    onSelectionChange(new Set());
    loadEntries(root);
  }, [loadEntries, onSelectionChange]);

  // Handle file selection (ZIPs, JSONs, etc.)
  const handleSelectFile = useCallback(async () => {
    const api = window.electronImport;
    if (!api) return;

    const files = await api.selectFile();
    if (!files || files.length === 0) return;

    // Go straight to detection for selected files
    onDetectFiles(files);
  }, [onDetectFiles]);

  // Navigate into a directory
  const handleNavigate = useCallback((dirPath: string) => {
    if (currentPath) {
      setPathHistory(prev => [...prev, currentPath]);
    }
    setCurrentPath(dirPath);
    loadEntries(dirPath);
  }, [currentPath, loadEntries]);

  // Go back
  const handleBack = useCallback(() => {
    const prev = pathHistory[pathHistory.length - 1];
    if (!prev) return;
    setPathHistory(h => h.slice(0, -1));
    setCurrentPath(prev);
    loadEntries(prev);
  }, [pathHistory, loadEntries]);

  // Navigate to a breadcrumb segment
  const handleBreadcrumb = useCallback((targetPath: string) => {
    if (targetPath === currentPath) return;
    // Find position in history or path
    setPathHistory([]);
    setCurrentPath(targetPath);
    loadEntries(targetPath);
  }, [currentPath, loadEntries]);

  // Toggle file selection
  const handleToggleSelect = useCallback((filePath: string) => {
    const next = new Set(selectedPaths);
    if (next.has(filePath)) {
      next.delete(filePath);
    } else {
      next.add(filePath);
    }
    onSelectionChange(next);
  }, [selectedPaths, onSelectionChange]);

  // Select all files in view
  const handleSelectAll = useCallback(() => {
    const filePaths = filteredEntries
      .filter(e => !e.isDirectory)
      .map(e => e.path);
    const next = new Set(selectedPaths);
    filePaths.forEach(p => next.add(p));
    onSelectionChange(next);
  }, [filteredEntries, selectedPaths, onSelectionChange]);

  // Deselect all
  const handleDeselectAll = useCallback(() => {
    onSelectionChange(new Set());
  }, [onSelectionChange]);

  // Compute breadcrumb segments from rootPath to currentPath
  const breadcrumbs = React.useMemo(() => {
    if (!rootPath || !currentPath) return [];
    const rootParts = rootPath.split('/');
    const currentParts = currentPath.split('/');
    const crumbs: Array<{ label: string; path: string }> = [];

    for (let i = rootParts.length - 1; i < currentParts.length; i++) {
      crumbs.push({
        label: currentParts[i] || '/',
        path: currentParts.slice(0, i + 1).join('/'),
      });
    }
    return crumbs;
  }, [rootPath, currentPath]);

  // Total size summary
  const totalSize = React.useMemo(() => {
    let sum = 0;
    for (const entry of entries) {
      sum += entry.isDirectory ? (dirSizes.get(entry.path) || 0) : entry.size;
    }
    return sum;
  }, [entries, dirSizes]);

  // No root selected yet
  if (!rootPath) {
    return (
      <div className="import-browser">
        <div className="import-browser__empty">
          <div className="import-browser__empty-buttons">
            <button
              className="import-browser__select-btn"
              onClick={handleSelectFolder}
            >
              Select Folder
            </button>
            <button
              className="import-browser__select-btn import-browser__select-btn--secondary"
              onClick={handleSelectFile}
            >
              Select File
            </button>
          </div>
          <p className="import-browser__hint">
            Choose a folder to browse, or select archive files directly (ZIP, JSON).
            Supports ChatGPT, Claude, Gemini, Twitter, Facebook, Instagram, Reddit, and more.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="import-browser">
      {/* Toolbar */}
      <div className="import-browser__toolbar">
        <button
          className="import-browser__back-btn"
          onClick={handleBack}
          disabled={pathHistory.length === 0}
          title="Go back"
        >
          ←
        </button>
        <div className="import-browser__breadcrumbs">
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={crumb.path}>
              {i > 0 && <span className="import-browser__breadcrumb-sep">›</span>}
              <button
                className={`import-browser__breadcrumb ${crumb.path === currentPath ? 'import-browser__breadcrumb--active' : ''}`}
                onClick={() => handleBreadcrumb(crumb.path)}
              >
                {crumb.label}
              </button>
            </React.Fragment>
          ))}
        </div>
        <div className="import-browser__toolbar-spacer" />
        <button
          className="import-browser__change-btn"
          onClick={handleSelectFolder}
          title="Change folder"
        >
          Change Folder
        </button>
        <div className="import-browser__view-toggle">
          <button
            className={`import-browser__view-btn ${viewMode === 'treemap' ? 'import-browser__view-btn--active' : ''}`}
            onClick={() => setViewMode('treemap')}
            title="Treemap view"
          >
            ▦
          </button>
          <button
            className={`import-browser__view-btn ${viewMode === 'list' ? 'import-browser__view-btn--active' : ''}`}
            onClick={() => setViewMode('list')}
            title="List view"
          >
            ☰
          </button>
        </div>
      </div>

      {/* Filter chips */}
      {extensions.length > 1 && (
        <div className="import-browser__filters">
          <button
            className={`import-browser__filter-chip ${!filterExt ? 'import-browser__filter-chip--active' : ''}`}
            onClick={() => setFilterExt(null)}
          >
            All
          </button>
          {extensions.map(([ext, count]) => (
            <button
              key={ext}
              className={`import-browser__filter-chip ${filterExt === ext ? 'import-browser__filter-chip--active' : ''}`}
              onClick={() => setFilterExt(filterExt === ext ? null : ext)}
            >
              .{ext} ({count})
            </button>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="import-browser__error">{error}</div>
      )}

      {/* Main view */}
      <div className="import-browser__view">
        {loading ? (
          <div className="import-browser__loading">
            <div className="panel__loading-spinner" />
            <span>Reading directory...</span>
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="import-browser__empty-dir">
            No files found in this directory.
          </div>
        ) : viewMode === 'treemap' ? (
          <TreemapView
            entries={filteredEntries}
            dirSizes={dirSizes}
            selectedPaths={selectedPaths}
            onNavigate={handleNavigate}
            onToggleSelect={handleToggleSelect}
          />
        ) : (
          <FileListView
            entries={filteredEntries}
            dirSizes={dirSizes}
            selectedPaths={selectedPaths}
            onNavigate={handleNavigate}
            onToggleSelect={handleToggleSelect}
          />
        )}
      </div>

      {/* Status bar */}
      <div className="import-browser__status">
        <span className="import-browser__status-info">
          {entries.length} items · {formatSize(totalSize)}
        </span>
        <div className="import-browser__status-spacer" />
        {selectedPaths.size > 0 && (
          <>
            <span className="import-browser__status-selected">
              {selectedPaths.size} selected
            </span>
            <button
              className="import-browser__status-btn"
              onClick={handleDeselectAll}
            >
              Clear
            </button>
          </>
        )}
        <button
          className="import-browser__status-btn"
          onClick={handleSelectAll}
        >
          Select All Files
        </button>
        {currentPath && (
          <button
            className="import-browser__status-btn import-browser__detect-folder-btn"
            onClick={() => onDetectFolder(currentPath)}
          >
            Detect This Folder
          </button>
        )}
        <button
          className="import-browser__detect-btn"
          onClick={onDetect}
          disabled={selectedPaths.size === 0}
        >
          Detect Selected ({selectedPaths.size})
        </button>
      </div>
    </div>
  );
}

export default ImportBrowser;
